import { action, observable } from 'mobx';
import { PLAN_TERM_MONTHLY } from '~/common/utils/consts.js';
import { isBoolean, isString } from '~/common/utils/typeCheckers.js';
import isPlainObject from 'lodash/isPlainObject';
import { mapData } from '~/common/utils/mapData.js';
import { getLabelData, postTrial } from '~/common/services/sitecoreServices.js';
import trialObj from '~/common/utils/trialObj.js';

export default class CheckoutClass {
    constructor(rootStore) {
        this.formsStore = rootStore.formsStore;
        this.setLoading(false);
        this.loadLabelContent();
        this.decodeEmail();
    }

    // show page loading spinner
    @observable isLoading = true;
    // incorrect input global error
    @observable formError = false;
    // success redirect url
    @observable urlRedirect = 'http://www.gotomeeting.com';
    // to show spinner while form is loading
    @observable formProcessing = false;
    // brand colors
    @observable brand = document.body.classList[0];

    // all data must be defined here
    @observable trialData = {
        Headline: 'Get Your <span>Free</span> 14-Day Trial',
        ErrorGlobal: '\u003cp\u003eSomething doesn\u0027t look right. Please check that you\u0027ve entered everything correctly.\u003c/p\u003e',
        Subheadline: 'No credit card. No commitments.',
        FirstName: 'First Name',
        LastName: 'Last Name',
        Email: 'Business Email',
        Phone: 'Phone Number (Optional)',
        JobTitle: 'Job title (Optional)',
        Password: 'Create Password',
        SubmitButton: 'Sign Up',
        Terms: ' By clicking "Sign Up” I agree to the <a href="https://www.logmeininc.com/legal/terms-and-conditions" target="_blank">Terms of Service</a> and <a href="https://www.logmeininc.com/legal/privacy" target="_blank">Privacy Policy</a>. I want to receive promotional emails, including product updates, special offers and best practices, unless I',
        CompanySize: 'Company Size',
        OptOutPreference: 'I prefer not to receive promotional emails.',
        AdditionalText: 'Interested in buying?  Visit our <a href="http://www.gotomeeting.com/pricing" target="_blank">pricing page</a> or call us at <a href="tel:1-888-646-0014">1-888-646-0014</a>.',
        OptOut: 'opt out',
        Jobs: ['Consulting', 'Customer Service', 'Engineering', 'Executive Management', 'Finance Legal Administration', 'Help Desk', 'Human Resources', 'IT', 'Marketing PR', 'Operations', 'Professional Services', 'Sales Business Development', 'Training', 'Other', 'Telecommunications'],
        ErrorFirstName: 'Please enter your first name.',
        ErrorLastName: 'Please enter your last name.',
        ErrorEmail: 'Please enter a valid email address',
        ErrorPassword1: '8 - 32 Characters',
        ErrorPassword2: 'Contains at least 1 letter',
        ErrorPassword3: 'Contains at least 1 number',
        ErrorCompany: 'Please select your company size.',
        ErrorInvalidCharacter: 'Invalid character(s). Please use only letters, spaces, -, or \'.'
    };

    //tracking helpers
    @observable accountKey = '';
    wrapper = document.querySelector('#trialFormWrapper');
    tealiumTags = this.wrapper ? JSON.parse(this.wrapper.dataset.tealiumTags) : '';

    @observable optInByDefault = this.wrapper.dataset.optinbydefault ? JSON.parse(this.wrapper.dataset.optinbydefault) : true;

    @observable chopId = window.chopId ? window.chopId : 'undefined';


    // ACTIONS
    @action mapData(data) {
        this.trialData = mapData(this.trialData, data);
    }

    @action setFormError(bool) {
        this.formError = isBoolean(this.formError, bool);
    }

    @action setLoading(bool) {
        this.isLoading = isBoolean(bool, this.isLoading);
    }

    @action tracking() {
        // tracking
        if(window.fireTealiumLinkEvent) {
            window.fireTealiumLinkEvent('form complete', this.wrapper ? this.wrapper.dataset.formName : 'trial form');
            window.fireTealiumTryTrackingEvent(
                this.accountKey,
                this.tealiumTags.trackingProduct,
                this.tealiumTags.trackingProductDuration,
                this.tealiumTags.trackingPlanEntitlement,
                this.tealiumTags.trackingTemplate,
                this.tealiumTags.trackingItem,
                this.tealiumTags.trackingSection,
                this.tealiumTags.trackingContentType,
                this.tealiumTags.trackingPageset,
                this.tealiumTags.trackingConversionType,
                this.tealiumTags.trackingPlan,
                {
                    event_name: "try flow complete"
                }
            );
        }
    }

    @action existingUser(callback) {
        const accessToken = window.getValueFromHash ? window.getValueFromHash('access_token') : null;
        const trialSettingId = this.wrapper ? this.wrapper.dataset.trialSetting : null;

        if (accessToken && trialSettingId) {
            this.formProcessing = true;
            const formState = JSON.parse(decodeURIComponent(window.getValueFromHash("state"))),
                timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
                formData = {
                    ProductTrialSettingId: trialSettingId,
                    AccessToken: accessToken, // Setting the AccessToken indicates an existing user
                    TimeZone: timezone,
                    Phone: formState.Phone,
                    JobTitle: formState.JobTitle,
                    CompanySize: formState.CompanySize,
                    OptOut: !formState.OptOut
                };

            return postTrial(formData)
                .then( (response) => {
                    const data = response.data;
                    if (!data.ErrorCode) {
                        this.accountKey = data.AccountKey;
                        this.tracking();
                        this.urlRedirect = data.SuccessRedirectUrl;
                        callback(true);
                    } else {
                        this.formError = true;
                        this.formProcessing = false;
                        switch (data.ErrorCode) {
                            case 'COG_ERROR':
                            case 'INTERNAL_ERROR':
                                console.error(data.ErrorDetails);
                                break;
                        }
                        callback(false);
                    }
                })
                .catch( () => {
                        this.formProcessing = false;
                        this.formError = true;
                        callback(false);
                    }
                );
        }
    }

    // form submission
    @action formSubmit(callback) {
        const fields = [
            'FirstName',
            'LastName',
            'Email',
            'Password',
            'Radio'
        ];

        // validate the form
        this.formsStore.validateForm(fields);

        if (this.formsStore.doesFormHaveErrors(fields)) {
            this.formError = true;
            return false;
        }

        this.formProcessing = true;

        return postTrial(trialObj())
            .then( (response) => {
                const data = response.data;
                if (!data.ErrorCode) {
                    this.accountKey = data.AccountKey;
                    // tracking
                    this.tracking();
                    this.urlRedirect = data.SuccessRedirectUrl;
                    callback(true);
                } else {
                    this.formError = true;
                    switch (data.ErrorCode) {
                        case 'COG_ERROR':
                            console.error(data.ErrorDetail);
                            break;
                        case 'EXISTING_CROSS_PRODUCT_USER':
                            const oAuthRedirectUrl = this.wrapper ? this.wrapper.dataset.oauthRedirect : '';
                            const formState = {
                                Phone: this.formsStore.PhoneNumber,
                                JobTitle: this.formsStore.JobTitle,
                                CompanySize: this.formsStore.CompanySize,
                                OptOut: !this.formsStore.OptOut
                            };
                            window.getOAuthUrl(oAuthRedirectUrl,
                                function(data) {
                                    document.querySelector("#modalCrossProductLoginUrl").href = data;
                                    //pop modal
                                    document.querySelector("#modalCrossProductLink").click();
                                },
                                formState);
                            break;
                        case 'EXISTING_USER':
                            // open modal to login
                            document.querySelector("#modalExistingUserLink").click();
                            break;
                        case 'INTERNAL_ERROR':
                            console.error(data.ErrorDetails);
                            break;
                    }
                    callback(false);
                }
            })
            .catch( () => {
                    this.formProcessing = false;
                    this.formError = true;
                    callback(false);
                }
            );
    }

    @action decodeEmail(){
        let urlSplit = window.location.href.split("?");
        if (urlSplit.length > 1) {
            let querystringParams = urlSplit[1].split('&');
            for (let i = 0; i < querystringParams.length; i++) {
                let querystringParam = querystringParams[i].split('=');
                if (querystringParam[0] === 'hash') {
                    let encodedValue = querystringParam[1];
                    let decodedValue;
                    if (encodedValue !== '') {
                        try {
                            decodedValue = window.atob(encodedValue);
                        } catch (err) {
                            // if the hash is not base 64 exit
                            return;
                        }
                        let decodedValueSplit = decodedValue.split('=');
                        if (decodedValueSplit.length > 1 && decodedValueSplit[0] === 'Email') {
                            return this.formsStore.setFormData('Email', decodedValueSplit[1]);
                        }
                    }
                }
            }
        }
    }

    // SERVICES

    // API content for labels and errors
    @action loadLabelContent() {
        getLabelData()
            .then( resp => {
                if (isPlainObject(resp.data)) {
                    // map the loaded json to the correct stores
                    this.mapData(resp.data);
                }
                // show the app
                this.setLoading(false);
            })
            // backup data used
            .catch( (err) => {
                console.log('Using backup label content', err);
                this.setLoading(false);
            } );
    }


    // COMPUTED
}
